import {
  Edit,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Tag,
  Upload,
  UploadFile,
  UploadProps,
  useForm,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import { IResourceComponentsProps, useShow } from "@pankod/refine-core";
import { TRACK_TYPES } from "constants/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import "react-mde/lib/styles/css/react-mde-all.css";
import { IGenre } from "types/genre.interface";
import { ILabel } from "types/label.interface";
import { ISample } from "types/sample.interface";
import { ITag } from "types/tag.interface";
import { ITrack } from "types/track.interface";
import { ITrackKey, ITrackKeyResponse } from "types/trackkey.interface";
import { IUser } from "types/user.interface";
import { beforeUpload, formatDateTime, formatTime } from "utils";
import datetimeService from "services/datetime.service";

export const DraftTrackEdit: React.FC<IResourceComponentsProps> = () => {
  const [fileListArtwork, setFileListArtwork] = useState<UploadFile[]>();
  const [bmpStart, setBmpStart] = useState<number>(0);
  const [bmpEnd, setBmpEnd] = useState<number>(0);

  const [genre, setGenre] = useState<string>("");
  const [subGenre, setSubGenre] = useState<string>("");
  const [subGenre2, setSubGenre2] = useState<string>("");

  const { formProps, saveButtonProps, formLoading } = useForm<ITrack>();
  const { queryResult } = useShow<ITrack>();
  const { data, isLoading } = queryResult;
  const record: any = data?.data;

  const { tableProps } = useTable<ITrackKeyResponse>({
    resource: "track-keys",
    initialPageSize: 100,
  });

  const [camelotKey, setCamelotKey] = useState<string>();
  const [trackKeySelect, setTrackKeySelect] = useState<string>(
    record?.data?.trackKey._id
  );

  useEffect(() => {
    if (record?.data?.trackKey.camelotKey) {
      setCamelotKey(record?.data?.trackKey.camelotKey);
    }
  }, [record?.data?.trackKey.camelotKey]);

  const { selectProps: UserSelectProps } = useSelect<IUser>({
    resource: "users",
    fetchSize: 100,
    optionLabel: "username",
    optionValue: "_id",
  });

  const { selectProps: GenreSelectProps } = useSelect<IGenre>({
    resource: "genres",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });

  const selectedSubGenres = useMemo(
    () =>
      GenreSelectProps?.options?.filter(
        (option: any) => ![genre, subGenre2].includes(option.value)
      ),
    [GenreSelectProps?.options, genre, subGenre2]
  );

  const selectedGenres = useMemo(
    () =>
      GenreSelectProps?.options?.filter(
        (option: any) => ![subGenre, subGenre2].includes(option.value)
      ),
    [GenreSelectProps?.options, subGenre, subGenre2]
  );

  const selectedSubGenres2 = useMemo(
    () =>
      GenreSelectProps?.options?.filter(
        (option: any) => ![genre, subGenre2].includes(option.value)
      ),
    [GenreSelectProps?.options, genre, subGenre2]
  );

  const { selectProps: TrackKeySelectProps } = useSelect<ITrackKey>({
    resource: "track-keys",
    fetchSize: 100,
    optionLabel: "musicKey",
    optionValue: "_id",
  });

  const { selectProps: TagSelectProps } = useSelect<ITag>({
    resource: "tags",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });

  const { selectProps: SamplesSelectProps } = useSelect<ISample>({
    resource: "samples",
    fetchSize: 100,
    optionLabel: "track",
    optionValue: "_id",
  });

  const { selectProps: LabelSelectProps } = useSelect<ILabel>({
    resource: "labels",
    fetchSize: 100,
    optionLabel: "name",
    optionValue: "_id",
  });

  // const [rating, setRating] = useState(record?.data.contributorRating);

  // const onOptionChange = (e: any) => {
  //   setRating(e.target.value);
  // };

  // const isPrimetime = rating === "primetime";
  // const isOpening = rating === "opening";

  useEffect(() => {
    // if (record?.data) {
    //   setRating(record?.data.contributorRating);
    // }
    if (record?.data.artwork) {
      setFileListArtwork([
        { uid: "1", name: "avatar", url: record?.data.artwork },
      ]);
    }
    let trackTable: any = (tableProps?.dataSource || []).find(
      (trackKey: any) => trackKeySelect === trackKey._id
    );
    setCamelotKey(trackTable?.camelotKey);
    setBmpStart(record?.data.bpmStart);
    setBmpEnd(record?.data.bpmEnd);
    setGenre(record?.data?.genre?._id);
    setSubGenre(record?.data?.subGenre?._id);
    setSubGenre2(record?.data?.subGenre2?._id);
  }, [
    record?.data.artwork,
    record?.data.trackKey,
    tableProps?.dataSource,
    trackKeySelect,
    record?.data.bpmStart,
    record?.data.bpmEnd,
    record?.data?.genre?._id,
    record?.data?.subGenre?._id,
    record?.data?.subGenre2?._id,
    record?.data,
  ]);

  const handleChangeArtwork: UploadProps["onChange"] = ({
    fileList: newFileList,
  }) => setFileListArtwork(newFileList);

  const handleTrackKey = useCallback(
    (value: any) => setTrackKeySelect(value),
    []
  );

  return (
    <Edit
      saveButtonProps={saveButtonProps}
      isLoading={isLoading || formLoading}
    >
      <Form {...formProps} layout="vertical">
        <Form.Item
          label="Title"
          name="title"
          style={{ width: "40%" }}
          initialValue={record?.data.title}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Price"
          name="price"
          style={{ width: "20%" }}
          initialValue={record?.data.price}
        >
          <Input type="number" defaultValue={record?.data.price} />
        </Form.Item>
        <Form.Item
          label="Duration"
          name="duration"
          rules={[
            {
              required: false,
            },
          ]}
          style={{ width: "20%" }}
        >
          <Input
            disabled
            defaultValue={
              record?.data.duration && formatTime(record?.data.duration)
            }
          />
        </Form.Item>

        <div className="edit-title">
          <Form.Item
            className="edit-item w48"
            label="IsClean"
            name="isClean"
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Switch defaultChecked={record?.data.isClean} />
          </Form.Item>
          <Form.Item
            className="edit-item w48"
            label="IsOriginal"
            name="isOriginal"
            rules={[
              {
                required: false,
              },
            ]}
          >
            <Switch defaultChecked={record?.data.isOriginal} />
          </Form.Item>
        </div>
        <div className="edit-title">
          <Form.Item
            className="edit-item w48"
            label="BpmStart"
            name="bpmStart"
            rules={[
              {
                min: 50,
                type: "number",
                message: "BPM Start must be more than 50",
              },
              {
                max: 220,
                type: "number",
                message: "BPM Start must be less than BMP 220",
              },
              {
                max: bmpEnd,
                type: "number",
                message: "BPM Start must be less than BMP End",
              },
            ]}
            initialValue={record?.data.bpmStart}
          >
            <InputNumber
              defaultValue={record?.data.bpmStart}
              type="number"
              onChange={(value: number) => setBmpStart(value)}
            />
          </Form.Item>
          <Form.Item
            className="edit-item w48"
            label="BpmEnd"
            name="bpmEnd"
            rules={[
              {
                required: false,
              },
              {
                min: bmpStart + 1,
                type: "number",
                message: "BPM End must be more than BMP Start",
              },
              {
                min: 50,
                type: "number",
                message: "BPM End must be more than 50",
              },
              {
                max: 220,
                type: "number",
                message: "BPM End must be less than BMP 220",
              },
            ]}
          >
            <InputNumber
              defaultValue={record?.data.bpmEnd}
              type="number"
              onChange={(value: number) => setBmpEnd(value)}
            />
          </Form.Item>
        </div>

        <Form.Item
          label="Year"
          name="year"
          rules={[
            {
              min: 1950,
              max: datetimeService.currentYear(),
              type: "number",
              message: `Year must be between 1950 and ${datetimeService.currentYear()}`,
            },
          ]}
          initialValue={record?.data.year}
        >
          <InputNumber defaultValue={record?.data.year} type="number" />
        </Form.Item>

        <Form.Item label="Created" name="createdAt" key="createdAt">
          {formatDateTime(record?.data.createdAt)}
        </Form.Item>
        <Form.Item
          label="PreviewUrl"
          name="previewUrl"
          rules={[
            {
              required: false,
            },
          ]}
        >
          {record?.data.type === TRACK_TYPES.AUDIO ? (
            <audio controls src={record?.data.previewUrl} />
          ) : (
            <video controls src={record?.data.previewUrl} />
          )}
        </Form.Item>

        <Form.Item
          label="Artwork"
          name="artwork"
          rules={[
            {
              required: false,
            },
          ]}
          style={{ marginBottom: "30px", width: "30%" }}
        >
          <Upload
            accept="image/*"
            name="artwork"
            listType="picture-card"
            fileList={fileListArtwork}
            beforeUpload={beforeUpload}
            onChange={handleChangeArtwork}
            maxCount={1}
          >
            change
          </Upload>
        </Form.Item>
        <Form.Item
          label="Artist"
          name="artist"
          style={{ width: "40%" }}
          initialValue={record?.data.artist}
        >
          <Input defaultValue={record?.data.artist} />
        </Form.Item>
        <Form.Item
          label="Owner"
          name="user"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            {...UserSelectProps}
            defaultValue={record?.data?.user?.username}
          />
        </Form.Item>
        <Form.Item
          label="Genre"
          name="genre"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            options={selectedGenres}
            defaultValue={record?.data?.genre?.name}
            onChange={(value: any) => setGenre(value)}
          />
        </Form.Item>
        <Form.Item
          label="SubGenre"
          name="subGenre"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            options={selectedSubGenres}
            defaultValue={record?.data?.subGenre?._id}
          />
        </Form.Item>
        <Form.Item
          label="SubGenre2"
          name="subGenre2"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            options={selectedSubGenres2}
            defaultValue={record?.data?.subGenre2?._id}
          />
        </Form.Item>
        <Form.Item
          label="TrackKey"
          name="trackKey"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            {...TrackKeySelectProps}
            onChange={handleTrackKey}
            defaultValue={record?.data?.trackKey?.musicKey}
          />
        </Form.Item>
        <Form.Item
          label="CamelotKey"
          name="camelotKey"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Tag>{camelotKey || record?.data?.trackKey.camelotKey}</Tag>
        </Form.Item>

        <Form.Item
          label="Tags"
          name="tags"
          initialValue={record?.data?.tags?.map((tag: ITag) => tag?._id)}
        >
          <Select
            mode="multiple"
            {...TagSelectProps}
            defaultValue={record?.data?.tags?.map((tag: ITag) => tag?._id)}
          />
        </Form.Item>
        <Form.Item
          label="Samples"
          name="samples"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            mode="multiple"
            {...SamplesSelectProps}
            defaultValue={record?.data?.samples?.map(
              (sample: ISample) => sample._id
            )}
          />
        </Form.Item>
        <Form.Item
          label="Label"
          name="label"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <Select
            {...LabelSelectProps}
            defaultValue={record?.data?.label?.name}
          />
        </Form.Item>

        {/* <Form.Item
          label="ContributorRating"
          name="contributorRating"
          rules={[
            {
              required: false,
            },
          ]}
        >
          <div style={{display: 'flex', gap: '10px'}}>
            <input
              type="radio"
              name="topping"
              value="opening"
              id="opening"
              checked={rating === "opening"}
              onChange={onOptionChange}
              style={{display:'none'}}
            />
            <label 
              htmlFor="opening"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '5px',
                marginRight: '5px',
                padding: '0px 30px',
                height: '40px',
                borderWidth: '1px',
                backgroundColor: isOpening ? '#73ff02' : '#f7f7f7',
                borderColor: isOpening ? '#73ff02' : '#bbb',
                color: isOpening ? '#000' : '#9c9c9c',
              }}
            >
              Opening
            </label>

            <input
              type="radio"
              name="topping"
              value="primetime"
              id="primetime"
              checked={rating === "primetime"}
              onChange={onOptionChange}
              style={{display:'none'}}
            />
            <label 
              htmlFor="primetime"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '5px',
                marginRight: '5px',
                padding: '0px 30px',
                height: '40px',
                borderWidth: '1px',
                backgroundColor: isPrimetime ? '#73ff02' : '#f7f7f7',
                borderColor: isPrimetime ? '#73ff02' : '#bbb',
                color: isPrimetime ? '#000' : '#9c9c9c',
              }}
            >
              Primetime
            </label>
          </div>
        </Form.Item> */}
      </Form>
    </Edit>
  );
};
